import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, UserCheck, X } from "lucide-react";

export function AssignGuest({ booking, onChanged }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/users/search", { params: { q } }); setResults(data); } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const assign = async (user_id) => {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.booking_id}/assign`, { user_id });
      toast.success(user_id ? "Client associé au séjour" : "Association retirée");
      setOpen(false); setQ(""); onChanged?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      {booking.traveler_id ? (
        <span className="inline-flex items-center gap-1 text-xs text-olive" data-testid={`assigned-${booking.booking_id}`}>
          <UserCheck className="w-3.5 h-3.5" /> Compte client associé
          <button type="button" onClick={() => assign(null)} disabled={busy} className="ml-1 text-muted-foreground hover:text-destructive" title="Retirer l’association" data-testid={`unassign-${booking.booking_id}`}><X className="w-3.5 h-3.5" /></button>
        </span>
      ) : (
        <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-xs text-rose-brand font-semibold hover:underline" data-testid={`assign-open-${booking.booking_id}`}>
          <UserPlus className="w-3.5 h-3.5" /> Associer un client
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-olive">Associer un compte client</DialogTitle>
            <DialogDescription>{booking.listing_title} · {booking.traveler_name} · {booking.check_in} → {booking.check_out}</DialogDescription>
          </DialogHeader>
          <Input autoFocus placeholder="Nom, email ou téléphone du client…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="assign-search" />
          <div className="max-h-64 overflow-y-auto divide-y divide-border" data-testid="assign-results">
            {q.trim().length >= 2 && results.length === 0 && <p className="text-sm text-muted-foreground py-3">Aucun compte trouvé. Le client doit d’abord créer son compte dans l’app.</p>}
            {results.map((u) => (
              <div key={u.user_id} className="flex items-center justify-between py-2 gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{u.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</p>
                </div>
                <Button size="sm" disabled={busy} onClick={() => assign(u.user_id)} className="bg-olive text-white shrink-0" data-testid={`assign-pick-${u.user_id}`}>Associer</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
